<script setup lang="ts">
  import { computed, getCurrentInstance, onMounted, ref } from 'vue';
  import OptionsService from '@/api/services/options.service';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosUiSegmented from '@/components/pos-v2/ui/PosUiSegmented.vue';
  import { useModal } from '@/composition/use-modal';
  import { capitalize } from '@/domain/catalog/catalog-status-text';
  import {
    GROUP_TEMPLATES,
    TGroupTemplate,
  } from '@/domain/catalog/group-templates';
  import {
    groupRuleText,
    linkableGroups,
    linksToCopy,
    TGroupPickerResult,
  } from '@/domain/catalog/option-groups';
  import { normalizeText } from '@/domain/pos/search-normalize';
  import { IItem } from '@/interfaces/IItem';
  import { IOption } from '@/interfaces/IOption';

  /**
   * «+ Agregar grupo de opciones» (plan §5.13): create a group from a
   * template, use (or copy) a group the store already has, or copy the
   * groups of another product. Resolves what to add, or null.
   */
  const props = defineProps<{
    /** The vue-js-modal name it was opened with. */
    dialogName: string;
    restaurantId: number;
    branchId: number;
    /** The product's menu (null = the base menu). */
    menuId?: number | null;
    /** Groups already linked to the product. */
    linkedIds: number[];
    /** Same-menu products with groups (for «Copiar de otro producto»). */
    products: IItem[];
    /** Group id → products that use it. */
    usage: Record<number, number[]>;
  }>();

  type TPath = 'new' | 'existing' | 'product';

  const modals = useModal();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const tc = (key: string, count: number, params?: Record<string, unknown>) =>
    String(proxy.$tc(key, count, params));

  const path = ref<TPath>('new');
  const query = ref('');
  const groups = ref<IOption[]>([]);
  const loading = ref(false);
  const failed = ref(false);

  const paths = computed(() => [
    { id: 'new', label: t('menuPage.groups.picker.new') },
    { id: 'existing', label: t('menuPage.groups.picker.existing') },
    { id: 'product', label: t('menuPage.groups.picker.product') },
  ]);

  const shownGroups = computed(() => {
    const text = normalizeText(query.value);
    const list = linkableGroups(
      groups.value,
      props.menuId ?? null,
      props.linkedIds
    );
    return text
      ? list.filter((group) => normalizeText(group.title).includes(text))
      : list;
  });

  const shownProducts = computed(() => {
    const text = normalizeText(query.value);
    return props.products
      .map((product) => ({
        product,
        count: linksToCopy(product, props.menuId ?? null, props.linkedIds)
          .length,
      }))
      .filter(
        (entry) =>
          entry.count > 0 &&
          (!text || normalizeText(entry.product.name).includes(text))
      );
  });

  function ruleOf(group: IOption): string {
    const rule = groupRuleText({
      type: group.type,
      multiple: group.multiple,
      mandatory: group.mandatory,
      min: group.min ?? null,
      max: group.max ?? null,
    });
    return capitalize(t(rule.key, rule.params));
  }

  function choicesPreview(group: IOption): string {
    const titles = (group.optionItems ?? []).map((choice) => choice.title);
    const first = titles.slice(0, 3).join(', ');
    return titles.length > 3
      ? t('menuPage.groups.picker.andMore', {
          list: first,
          n: titles.length - 3,
        })
      : first;
  }

  function usedIn(group: IOption): number {
    return group.id !== undefined ? props.usage[group.id]?.length ?? 0 : 0;
  }

  function setPath(next: string) {
    path.value = next === 'existing' || next === 'product' ? next : 'new';
    query.value = '';
  }

  async function loadGroups() {
    loading.value = true;
    failed.value = false;
    try {
      groups.value = await OptionsService.getAllUsable(
        props.restaurantId,
        props.branchId,
        'all',
        props.menuId ?? null
      );
    } catch (error) {
      console.error('Loading option groups failed', error);
      failed.value = true;
    } finally {
      loading.value = false;
    }
  }

  function answer(result: TGroupPickerResult | null) {
    // Closes and unmounts the dialog: nothing may run after this.
    modals.dialogSubmit(props.dialogName, result);
  }

  function pickTemplate(template: TGroupTemplate) {
    answer({ kind: 'template', template });
  }

  function copyProduct(product: IItem) {
    answer({
      kind: 'fromProduct',
      links: linksToCopy(product, props.menuId ?? null, props.linkedIds),
    });
  }

  // Focus inside on open: Esc closes it and Tab starts here.
  const root = ref<HTMLElement | null>(null);
  onMounted(() => {
    root.value?.focus({ preventScroll: true });
    void loadGroups();
  });
</script>

<template>
  <div
    ref="root"
    class="v2-catalog-group-picker-dialog"
    role="dialog"
    tabindex="-1"
    :aria-label="t('menuPage.groups.add')"
    @keydown.esc.stop="answer(null)"
  >
    <h2 class="picker-title">{{ t('menuPage.groups.add') }}</h2>
    <PosUiSegmented
      class="picker-paths"
      :value="path"
      :items="paths"
      block
      @input="setPath"
    />

    <!-- Crear un grupo nuevo -->
    <div v-if="path === 'new'" class="picker-list">
      <button
        v-for="template in GROUP_TEMPLATES"
        :key="template"
        type="button"
        class="picker-row"
        @click="pickTemplate(template)"
      >
        <span class="row-text">
          <span class="row-title">{{
            t(`menuPage.groups.template.${template}`)
          }}</span>
          <span class="row-meta">{{
            t(`menuPage.groups.template.${template}Hint`)
          }}</span>
        </span>
        <i class="mdi mdi-chevron-right row-chevron" />
      </button>
    </div>

    <template v-else>
      <PosUiInput
        v-model="query"
        class="picker-search"
        icon="magnify"
        clearable
        :placeholder="
          t(
            path === 'existing'
              ? 'menuPage.groups.picker.searchGroups'
              : 'menuPage.groups.picker.searchProducts'
          )
        "
        :aria-label="
          t(
            path === 'existing'
              ? 'menuPage.groups.picker.searchGroups'
              : 'menuPage.groups.picker.searchProducts'
          )
        "
      />

      <!-- Usar un grupo que ya tengo -->
      <div v-if="path === 'existing'" class="picker-list">
        <p v-if="loading" class="picker-empty">
          {{ t('menuPage.groups.picker.loading') }}
        </p>
        <div v-else-if="failed" class="picker-empty">
          {{ t('menuPage.groups.picker.failed') }}
          <PosUiButton color="neutral" variant="ghost" @click="loadGroups">{{
            t('menuPage.states.retry')
          }}</PosUiButton>
        </div>
        <p v-else-if="!shownGroups.length" class="picker-empty">
          {{ t('menuPage.groups.picker.noGroups') }}
        </p>
        <div
          v-for="group in shownGroups"
          v-else
          :key="group.id"
          class="group-row"
        >
          <div class="row-text">
            <span class="row-title">{{ group.title }}</span>
            <span class="row-meta">{{ ruleOf(group) }}</span>
            <span v-if="choicesPreview(group)" class="row-meta">{{
              choicesPreview(group)
            }}</span>
            <span v-if="usedIn(group)" class="row-meta">{{
              tc('menuPage.groups.usedIn', usedIn(group), { n: usedIn(group) })
            }}</span>
          </div>
          <div class="row-actions">
            <PosUiButton
              color="neutral"
              variant="outline"
              @click="answer({ kind: 'use', option: group })"
              >{{ t('menuPage.groups.picker.use') }}</PosUiButton
            >
            <PosUiButton
              color="neutral"
              variant="ghost"
              @click="answer({ kind: 'copy', option: group })"
              >{{ t('menuPage.groups.picker.useCopy') }}</PosUiButton
            >
          </div>
        </div>
      </div>

      <!-- Copiar de otro producto -->
      <div v-else class="picker-list">
        <p v-if="!shownProducts.length" class="picker-empty">
          {{ t('menuPage.groups.picker.noProducts') }}
        </p>
        <button
          v-for="entry in shownProducts"
          :key="entry.product.id"
          type="button"
          class="picker-row"
          @click="copyProduct(entry.product)"
        >
          <span class="row-text">
            <span class="row-title">{{ entry.product.name }}</span>
            <span class="row-meta">{{
              tc('menuPage.groups.count', entry.count, { n: entry.count })
            }}</span>
          </span>
          <i class="mdi mdi-chevron-right row-chevron" />
        </button>
      </div>
    </template>

    <div class="picker-actions">
      <PosUiButton color="neutral" variant="ghost" @click="answer(null)">{{
        $t('cancel')
      }}</PosUiButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-catalog-group-picker-dialog {
    @apply tw-flex tw-flex-col tw-bg-surface tw-px-5 tw-pb-4 tw-pt-5;

    // Focused on open (no ring: it is the dialog itself).
    &:focus {
      @apply tw-outline-none;
    }

    .picker-title {
      @apply tw-m-0 tw-text-base tw-font-semibold tw-leading-snug tw-text-neutral-900;
    }

    .picker-paths {
      @apply tw-mt-3;
    }

    .picker-search {
      @apply tw-mt-3;
    }

    .picker-list {
      @apply tw-mt-3 tw-overflow-y-auto tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200;
      max-height: 380px;
    }

    .picker-row,
    .group-row {
      @apply tw-flex tw-min-h-[56px] tw-w-full tw-items-center tw-border-0 tw-border-b tw-border-solid tw-border-neutral-100 tw-bg-surface tw-px-3 tw-py-2 tw-text-left;

      &:last-child {
        @apply tw-border-b-0;
      }
    }

    .picker-row {
      @apply tw-cursor-pointer;

      &:hover {
        @apply tw-bg-neutral-50;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-inset tw-ring-orange-500/40;
      }
    }

    .row-text {
      @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-col;

      .row-title {
        @apply tw-truncate tw-text-sm tw-font-semibold tw-text-neutral-900;
      }

      .row-meta {
        @apply tw-truncate tw-text-xs tw-text-neutral-500;
      }
    }

    .row-chevron {
      @apply tw-ml-2 tw-text-xl tw-leading-none tw-text-neutral-400;
    }

    // Margins, not flex gap (Chrome < 84).
    .row-actions {
      @apply tw-ml-2 tw-flex tw-flex-none tw-flex-col tw-items-stretch;

      > * + * {
        @apply tw-mt-1;
      }
    }

    .picker-empty {
      @apply tw-m-0 tw-px-3 tw-py-4 tw-text-sm tw-text-neutral-500;
    }

    .picker-actions {
      @apply tw-mt-4 tw-flex tw-justify-end;
    }
  }
</style>
